import Link from 'next/link'
import classNames from 'classnames'
import { FC, useEffect, useState } from 'react'

import NavLogo from './NavLogo'
import BagIcon from './BagIcon'
import Icon from 'components/Icon'
import { Router } from 'next/router'
import { Project } from 'common/project'
import Strings from 'project/localisation'
import Accordion from 'components/Accordion'
import { PlanLinks } from 'common/planSlugs'
import PreviewBar from 'components/PreviewBar'
import HeaderNavButton from './HeaderNavButton'
import { THeaderLinkGroup, TLink } from 'common/cms-types'
import TrustedPartnershipBanner from './TrustedPartnershipBanner'
import { businessBankingStrings } from 'common/strings/business-banking-strings'
import { Tenant } from 'common/tenant'

declare const Chargebee: any

type HeaderType = {
  transparentHeader?: boolean
  tenant?: Tenant
}

export function officeLinks() {
  return [
    {
      href: '/membership',
      title: `${Strings.membership}`,
    },
    {
      href: '/paul-street-office',
      title: `${Strings.paulStreet}`,
    },
    {
      href: '/private-office-suites',
      title: `${Strings.privateOffices}`,
    },
  ] as TLink[]
}

export function serviceLinks() {
  return [
    {
      href: '/free-accounting-service',
      subtitle: 'Get a free accounting service when you buy a Virtual Office.',
      title: `${Strings.freeAccountingService}`,
    },
    {
      href: '/free-business-banking',
      subtitle: `Open a business bank account and get ${businessBankingStrings.tideCashBackAmount} cashback with Tide.`,
      title: `${Strings.freeBusinessBanking}`,
    },
  ] as TLink[]
}

export function enterpriseSystemLinks(au: boolean) {
  const links = [
    {
      href: au ? '/au/enterprise' : '/enterprise',
      subtitle: 'Offer virtual office solutions to your clients.',
      title: `Registered Address as a Service`,
    },
    {
      href: au ? '/au/partner-integrations' : '/partner-integrations',
      subtitle: 'Build on our platform and make it your own.',
      title: `API & White-Label Integration`,
    },
  ] as TLink[]
  if (!au) {
    links.push({
      href: '/solutions-for-accountants',
      subtitle: 'Eliminate registered office admin for your practice.',
      title: 'Solutions for Accountants',
    })
  }
  return links
}

export function productLinks() {
  const links = [
    {
      href: `/${PlanLinks.SCAN_LITE}`,
      label: 'Most Popular',
      subtitle: 'Prime address with free mail scanning included.',
      title: `${Strings.virtualOfficeScanLiteLink}`,
    },
    {
      href: `/whatsapp-business-number`,
      subtitle: 'A dedicated business number, with built-in customer service.',
      title: `${Strings.virtualOfficePhone}`,
    },
    {
      groupTitle: Strings.addons,
      href: `/${PlanLinks.SCAN_PRO}`,
      subtitle: 'Forward your physical post anywhere in the UK.',
      title: `${Strings.virtualOfficeScanProLink}`,
    },
  ] as TLink[]
  return links
}

export function customerCareLinks() {
  return [
    {
      href: 'https://help.hoxtonmix.com/en/',
      title: `${Strings.helpAndAdvice}`,
    },
    {
      href: '/contact-us',
      title: `${Strings.contactUs}`,
    },
  ] as TLink[]
}

// The AU site is otherwise only reachable from deep inside the UK pages; linking
// it from the footer puts /au/ one click from every page on the site.
// reload: switching country must be a full page load. Some values (default
// planPrice, tideCashBackAmount) resolve the tenant once from the URL and
// would otherwise keep the previous country's currency after navigating.
export function regionLinks() {
  return [
    {
      href: '/',
      reload: true,
      title: `${Strings.unitedKingdom}`,
    },
    {
      href: '/au/',
      reload: true,
      title: `${Strings.australia}`,
    },
  ] as TLink[]
}

export function legalLinks(au: boolean) {
  if (au) {
    return [
      {
        href: '/au/privacy-policy',
        title: `${Strings.privacyPolicy}`,
      },
      {
        href: '/au/terms-and-conditions',
        title: `${Strings.termsAndConditions}`,
      },
    ] as TLink[]
  }
  return [
    {
      href: 'https://help.hoxtonmix.com/en/category/privacy-centre-1lueq1n/',
      target: '_blank',
      title: `${Strings.privacyPolicy}`,
    },
    {
      href: '/terms-and-conditions',
      title: `${Strings.termsAndConditions}`,
    },
    {
      href: 'https://help.hoxtonmix.com/en/category/kyc-compliance-24fnge/',
      target: '_blank',
      title: `${Strings.kycRequirements}`,
    },
  ] as TLink[]
}

export function companyLinks(isHeader: boolean) {
  return [
    {
      href: '/blog',
      title: `${Strings.blog}`,
    },
    {
      href: '/about-us',
      title: `${Strings.aboutUs}`,
    },
    {
      href: '/social-impact',
      title: `${Strings.socialImpact}`,
    },
    {
      href: '/business-support',
      title: `${Strings.businessSupport}`,
    },
    {
      href: '/silicon-roundabout',
      title: `${Strings.siliconRoundabout}`,
    },
    {
      href: '/outreach',
      title: `${Strings.outreach}`,
    },
    isHeader
      ? null
      : {
          href: 'https://status.hoxtonmix.com/',
          title: `${Strings.uptimeAndStatus}`,
        },
    {
      href: '/mobile',
      title: `${Strings.hoxtonMixApp}`,
    },
  ].filter((v) => !!v) as TLink[]
}

function headerLinks(au: boolean) {
  if (au) {
    return [
      {
        groups: [],
        href: '/au/virtual-office-sydney',
        title: `Virtual Office`,
      },
      {
        groups: [
          {
            links: enterpriseSystemLinks(true),
            title: `Partner Solutions`,
          },
        ],
        showEnterprise: true,
        title: `Partner Network`,
      },
      {
        groups: [
          {
            links: [
              {
                href: '/au/about-us',
                title: 'About Us',
              },
              {
                href: '/au/contact-us',
                title: 'Contact Us',
              },
            ],
          },
        ],
        title: `Company`,
      },
    ] as THeaderLinkGroup[]
  }
  const links = [
    {
      groups: [
        {
          links: productLinks(),
          title: `${Strings.products}`,
        },
        {
          links: serviceLinks(),
          title: Strings.services,
        },
      ],
      href: '/virtual-office-london-old-street',
      title: `${Strings.virtualOfficeProducts}`,
    },
    {
      groups: [
        {
          links: enterpriseSystemLinks(false),
          title: `${Strings.enterpriseSystemsSubHeading}`,
        },
        {
          links: [],
          title: 'Partnership',
        },
      ],
      showEnterprise: true,
      title: `${Strings.enterpriseSystems}`,
    },
    {
      groups: [
        {
          links: companyLinks(true),
        },
      ],
      title: `${Strings.company}`,
    },
    Project.flags.market_place && {
      groups: [],
      href: '/marketplace',
      title: `${Strings.marketPlace}`,
    },
  ] as THeaderLinkGroup[]

  return links.filter((link) => !!link)
}

export function mobileHeaderLinks(au: boolean) {
  if (au) {
    return [
      {
        groups: [
          {
            links: enterpriseSystemLinks(au),
            title: `Partner Solutions`,
          },
        ],
        title: 'Partner Network',
      },
      {
        groups: [
          {
            links: [
              {
                href: '/au/about-us',
                title: 'About Us',
              },
              {
                href: '/au/contact-us',
                title: 'Contact Us',
              },
            ],
            title: '',
          },
        ],
        title: 'Company',
      },
      {
        groups: [
          {
            links: legalLinks(au),
            title: '',
          },
        ],
        title: Strings.legalInformation,
      },
    ]
  }
  return [
    {
      groups: [
        {
          links: productLinks(),
          title: Strings.products,
        },

        {
          links: serviceLinks(),
          title: Strings.services,
        },
      ],
      title: Strings.virtualOfficeProducts,
    },
    {
      groups: [
        {
          links: enterpriseSystemLinks(false),
          title: `${Strings.enterpriseSystemsSubHeading}`,
        },
      ],
      title: Strings.enterpriseSystems,
    },
    {
      groups: [
        {
          links: companyLinks(true),
          title: null,
        },
      ],
      title: Strings.company,
    },
  ] as THeaderLinkGroup[]
}

let chargebeePromise: Promise<any> | null = null
const loadChargebee = () => {
  if (chargebeePromise) {
    return chargebeePromise
  }
  chargebeePromise = new Promise((resolve) => {
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = 'https://js.chargebee.com/v2/chargebee.js'
    script.setAttribute('data-cb-site', Project.chargbeeSite)
    script.setAttribute('data-cb-domain', Project.chargebeeUrl)
    script.setAttribute('data-cb-gtm-enabled', 'true')
    script.setAttribute('data-cfasync', 'false')
    script.onload = () => {
      const cbInstance = Chargebee.init({
        domain: Project.chargebeeUrl,
        site: Project.chargbeeSite,
      })
      resolve(cbInstance)
    }
    document.head.appendChild(script)
  })
  return chargebeePromise
}

let _cbInstance: any

const Header: FC<HeaderType> = ({ tenant = Tenant.UK, transparentHeader }) => {
  const au = tenant === Tenant.AU
  const [collapsed, setCollapsed] = useState(true)

  useEffect(() => {
    if (!collapsed) {
      document.body.classList.add('modal-open')
    } else {
      document.body.classList.remove('modal-open')
    }
  }, [collapsed])

  useEffect(() => {
    Router.events.on('routeChangeComplete', () => {
      setCollapsed(true)
    })
  }, [])

  const onLogin = async () => {
    if (Project.auth.cognito) {
      document.location.href = Project.portalURL
    } else {
      _cbInstance = _cbInstance || (await loadChargebee())
      _cbInstance.createChargebeePortal().open()
    }
  }

  return (
    <>
      <nav
        className={classNames('navbar navbar-expand-lg', {
          'bg-body': !transparentHeader,
        })}
      >
        <div className='px-2  d-flex justify-content-between w-100 align-items-center px-lg-10 px-xxl-14'>
          {collapsed ? (
            <Link aria-label={Strings.home} href={au ? '/au' : '/'}>
              <NavLogo className='me-lg-8 me-0' />
            </Link>
          ) : (
            <div />
          )}

          <div className='d-flex gap-5 d-lg-none'>
            {collapsed ? (
              <Link
                aria-label={Strings.checkout}
                className='a-unstyled d-flex'
                prefetch={false}
                href={au ? '/au/checkout' : '/checkout'}
              >
                <div className='nav-item nav-link-title cursor-pointer position-relative px-0 mx-0'>
                  <span className='position-relative'>
                    <Icon name='basket' />
                    <span className='badge position-absolute start-100 translate-middle'>
                      <BagIcon />
                    </span>
                  </span>
                </div>
              </Link>
            ) : (
              <div />
            )}

            <button
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
              }}
              onClick={() => {
                setCollapsed(!collapsed)
              }}
              aria-expanded={!collapsed}
              aria-controls='mobile-menu'
              aria-label={Strings.toggleNav}
              className='nav-item d-flex d-lg-none nav-link-title cursor-pointer position-relative px-0 mx-0'
            >
              <span className='mt-1 position-relative'>
                {!collapsed ? (
                  <Icon fill='black1' name='close' />
                ) : (
                  <Icon fill='black1' name='hamburger-menu' />
                )}
              </span>
            </button>
          </div>

          {/*Desktop Nav Items*/}
          <div className='navbar-collapse d-none d-lg-block' id='navbarNav'>
            <div className='d-flex justify-content-between w-100'>
              <div>
                <div className='navbar-nav gap-7 d-flex align-items-center me-auto'>
                  {headerLinks(!!au).map((headerLinkGroup, i) => (
                    <HeaderNavButton
                      key={i}
                      headerLinkGroup={headerLinkGroup}
                    />
                  ))}
                </div>
              </div>
              <div className='navbar-nav gap-7 align-items-center d-flex justify-content-end flex-1'>
                <div
                  onClick={onLogin}
                  className='nav-item nav-link-title cursor-pointer'
                >
                  <div className='d-flex align-items-center nav-link-title'>
                    <Icon
                      className='me-2'
                      width={20}
                      height={20}
                      name='account'
                      fill='black1'
                    />
                    {Strings.login}
                  </div>
                </div>
                <Link
                  aria-label={Strings.checkout}
                  className='a-unstyled d-flex'
                  prefetch={false}
                  href={au ? '/au/checkout' : '/checkout'}
                >
                  <div className='nav-item nav-link-title cursor-pointer position-relative px-0 mx-0'>
                    <span className='position-relative'>
                      <Icon name='basket' />
                      <span className='badge position-absolute start-100 translate-middle'>
                        <BagIcon />
                      </span>
                    </span>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className='d-lg-none'>
        {!collapsed && (
          <div className='d-block d-lg-none' id='mobile-menu'>
            <div className='mobile-menu-container overflow-hidden mt-4  d-flex flex-1 justify-content-between flex-column container'>
              <div className='overflow-auto pe-6'>
                {au && (
                  <div className='collapsible pb-4'>
                    <Link
                      className='a-unstyled'
                      href={'/au/virtual-office-sydney'}
                    >
                      <span className='text-light cursor-pointer text-large  text-semibold text-body'>
                        Virtual Office
                      </span>
                    </Link>
                  </div>
                )}
                <Accordion
                  bodySize=''
                  chevronColour='#1d1d1d'
                  itemClassName='pb-3'
                  items={mobileHeaderLinks(!!au).map((v) => ({
                    body: (
                      <>
                        {v.groups.map((group) => (
                          <>
                            {!!group.title && (
                              <div className='text-muted fw-semibold my-2 d-block pb-md-2'>
                                {group.title}
                              </div>
                            )}
                            {group.links.map((link) => (
                              <Link
                                className='a-unstyled text-larges fw-semibold text-body'
                                key={link.title}
                                href={link.href}
                                target={link.target}
                                rel={
                                  link.target === '_blank'
                                    ? 'noopener noreferrer'
                                    : undefined
                                }
                              >
                                <div>
                                  <div className='link py-1 d-flex align-items-center cursor-pointer pb-md-2 ps-4'>
                                    {link.title}
                                    {link.label && (
                                      <>
                                        <span className='fs-12 ms-1 d-flex align-items-center py-half pe-0 px-2 bg-pink text-white fw-semibold rounded'>
                                          {link.label}
                                          <svg
                                            className=''
                                            xmlns='http://www.w3.org/2000/svg'
                                            width='16'
                                            height='16'
                                            viewBox='0 0 24 24'
                                            fill='none'
                                          >
                                            <path
                                              d='M17.7576 7.5791C17.3697 7.00941 16.9726 5.96823 16.7576 6.07899C16.4276 6.24899 16.4375 7.7291 16.5475 8.0791C16.6375 8.3491 16.7475 8.95918 16.5475 9.55918C16.4275 9.92918 16.2175 10.2092 15.9975 10.4192C15.7775 9.01918 15.2675 7.28918 14.1075 5.59918C13.2975 4.41918 12.2775 3.39918 11.0875 2.57918C10.8375 2.39918 10.0076 1.89899 9.75757 2.07899C9.49757 2.24899 9.86746 3.05918 9.93746 3.35918C10.1875 4.48918 10.1875 5.67918 9.93746 6.80918C9.77746 7.53918 9.52746 8.17918 9.23746 8.72918C9.05746 8.27918 8.78746 7.80918 8.36746 7.38918C7.54746 6.56918 6.05746 6.22918 6.05746 6.22918C6.05746 6.22918 4.92757 5.89894 4.75757 6.07894C4.58757 6.25894 5.03964 6.35804 5.21746 7.13918C5.49746 8.36918 5.49746 9.51918 5.21746 10.5492C5.10746 10.9692 4.96746 11.2892 4.82746 11.6192C4.61746 12.1092 4.40746 12.6192 4.25746 13.4392C4.20746 13.7292 3.95746 15.2292 4.25746 16.5692C4.83746 19.1192 7.53746 21.3992 10.5375 21.8592C10.5575 21.8592 10.5875 21.8592 10.6075 21.8592C10.9975 21.9392 11.3475 21.9592 11.6175 21.9592C11.8275 21.9592 11.9975 21.9492 12.0775 21.9392C12.1575 21.9392 12.2375 21.9092 12.3175 21.8992C15.2675 21.6492 17.6975 19.7292 18.8475 17.7192C19.8475 15.9692 19.8775 14.2392 19.8875 13.5892C19.9008 12.7212 19.1769 9.66355 17.7576 7.5791Z'
                                              fill='#FFFFFF'
                                            />
                                            <path
                                              d='M9.88452 17.0791H8.97925V15.2305H7.28296V17.0791H6.37476V12.7959H7.28296V14.4746H8.97925V12.7959H9.88452V17.0791Z'
                                              fill='white'
                                            />
                                            <path
                                              d='M14.3562 14.9316C14.3562 15.6406 14.1804 16.1855 13.8289 16.5664C13.4773 16.9473 12.9734 17.1377 12.3171 17.1377C11.6609 17.1377 11.157 16.9473 10.8054 16.5664C10.4539 16.1855 10.2781 15.6387 10.2781 14.9258C10.2781 14.2129 10.4539 13.6689 10.8054 13.2939C11.1589 12.917 11.6648 12.7285 12.323 12.7285C12.9812 12.7285 13.4841 12.918 13.8318 13.2969C14.1814 13.6758 14.3562 14.2207 14.3562 14.9316ZM11.2302 14.9316C11.2302 15.4102 11.321 15.7705 11.5027 16.0127C11.6843 16.2549 11.9558 16.376 12.3171 16.376C13.0417 16.376 13.4041 15.8945 13.4041 14.9316C13.4041 13.9668 13.0437 13.4844 12.323 13.4844C11.9617 13.4844 11.6892 13.6064 11.5056 13.8506C11.322 14.0928 11.2302 14.4531 11.2302 14.9316Z'
                                              fill='white'
                                            />
                                            <path
                                              d='M16.3962 17.0791H15.488V13.5518H14.325V12.7959H17.5593V13.5518H16.3962V17.0791Z'
                                              fill='white'
                                            />
                                          </svg>
                                        </span>
                                      </>
                                    )}
                                  </div>
                                  {link.subtitle && (
                                    <div className='fs-small mb-2 ps-4 text-muted fw-normal fs-14'>
                                      {link.subtitle}
                                    </div>
                                  )}
                                </div>
                              </Link>
                            ))}
                          </>
                        ))}
                        <hr className='mb-0' />
                      </>
                    ),
                    title: `${v.title}`,
                  }))}
                />
                {Project.flags.market_place && !au && (
                  <div className='collapsible pb-4'>
                    <Link className='a-unstyled' href={'/marketplace'}>
                      <span className='text-light cursor-pointer text-large  text-semibold text-body'>
                        {Strings.marketPlace}
                      </span>
                    </Link>
                  </div>
                )}
                {!au && (
                  <div className='collapsible pb-4'>
                    <span onClick={onLogin} className='a-unstyled'>
                      <span className='text-light cursor-pointer text-large  text-semibold text-body'>
                        {Strings.login}
                      </span>
                    </span>
                  </div>
                )}

                <div className='d-block d-md-none'>
                  <TrustedPartnershipBanner />
                </div>
              </div>
              <div className='d-none d-md-block'>
                <TrustedPartnershipBanner />
              </div>
            </div>
          </div>
        )}
      </div>
      <PreviewBar />
    </>
  )
}

export default Header
